// src/context/catalogoProductosCambios.js
//
// LA LISTA DE PRODUCTOS DEL NAVEGADOR "POR CAMBIOS" (13/10/2026 — Toto: "1 y 2"; docs/planes/COSTOS_GOOGLE_CLOUD.md §6.5).
//
// POR QUÉ: CatalogosContext guarda la lista de productos en el navegador (localStorage). Hasta el 13/10/2026, cada pestaña
// que se abría con la lista de más de 30 minutos bajaba los ~4,000 productos completos: el 70% de lo que lee el ERP
// (≈ US$7 al mes; 155 veces en día y medio de oficina, una cada ~74 minutos por colaborador).
// AHORA (con el interruptor `configuracion/catalogo_productos.porCambios` prendido): al abrir la pestaña se pregunta
// "¿qué productos cambiaron desde la última vez?" y se bajan solo esos. Lo sabe por la marca `catalogoCambioEn`, que el
// servidor le pone a cada producto cuando cambia CUALQUIER campo (functions/catalogos.js). Red de seguridad: la lista
// completa se vuelve a bajar una vez al día (y si algo falla al preguntar, también).
//
// Funciones PURAS (sin Firebase), con pruebas en catalogoProductosCambios.test.js. Las usa CatalogosContext.js.

// 🔴 El mismo nombre que en functions/lib/marcaCatalogo.js (CAMPO_MARCA). Cambiar uno = cambiar el otro.
export const CAMPO_MARCA = 'catalogoCambioEn';

// Red de seguridad: pasado este tiempo desde la última lista COMPLETA, se baja completa otra vez (≈ 1 vez al día por
// navegador). Arregla cualquier cambio que no se haya marcado y los productos borrados con un script.
export const VIDA_LISTA_COMPLETA_MS = 24 * 60 * 60 * 1000;

// Se vuelven a pedir los productos marcados en el último minuto antes de la marca más nueva vista (cuestan 0 a 2
// lecturas): cubre dos marcas casi a la vez, sin depender del orden exacto en que el servidor las guarda.
export const MARGEN_MS = 60 * 1000;

/**
 * Milisegundos de una marca: un Timestamp de Firestore (`toMillis`) o el `{ seconds, nanoseconds }` en que queda
 * después de guardarse en el navegador. null si no es una fecha.
 */
export function msDeMarca(v) {
  if (!v || typeof v !== 'object') return null;
  let ms = null;
  if (typeof v.toMillis === 'function') ms = v.toMillis();
  else if (typeof v.seconds === 'number') ms = v.seconds * 1000 + Math.floor((Number(v.nanoseconds) || 0) / 1e6);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * El producto SIN la marca. La marca solo sirve para preguntar por cambios (su valor más nuevo va aparte, en los datos
 * de la lista) y no se guarda en el navegador: medido el 13/10/2026, la caché de catálogos ocupa el 52.9% del tope de
 * localStorage de Chrome, y la marca en los ~4,000 productos sumaría ~0.4 millones de caracteres. Así las pantallas
 * reciben los productos con los mismos campos que antes.
 */
export function sinMarca(p) {
  if (!p || typeof p !== 'object' || !Object.prototype.hasOwnProperty.call(p, CAMPO_MARCA)) return p;
  const copia = { ...p };
  delete copia[CAMPO_MARCA];
  return copia;
}

/** La marca más nueva de una lista de documentos (o `previa`, si es mayor). null si ninguno tiene marca. */
export function marcaMasNueva(docs, previa = null) {
  let max = Number.isFinite(previa) ? previa : null;
  for (const d of docs || []) {
    const ms = msDeMarca(d && d[CAMPO_MARCA]);
    if (ms !== null && (max === null || ms > max)) max = ms;
  }
  return max;
}

/**
 * El orden de siempre de la lista: por nombre, en español, sin distinguir mayúsculas ni tildes y con los números en orden
 * natural. Los empates, por id: es el mismo orden que daba la lista completa (Firestore la entrega por id).
 */
export function ordenarPorNombre(arr) {
  return [...arr].sort((a, b) =>
    (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base', numeric: true }) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

// Contenido comparable de un producto, como queda después de guardarse en el navegador (las fechas como
// `{ type, seconds, nanoseconds }`) y con las claves en orden.
function ordenarClaves(v) {
  if (Array.isArray(v)) return v.map(ordenarClaves);
  if (v && typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v).sort()) out[k] = ordenarClaves(v[k]);
    return out;
  }
  return v;
}
function comparable(p) {
  return JSON.stringify(ordenarClaves(JSON.parse(JSON.stringify(p))));
}

/**
 * Junta con la lista guardada los productos que llegaron (cada uno con su `id`): reemplaza el que ya estaba, saca los
 * que ya no están activos (igual que la lista completa: solo entran los `activo`) y agrega los nuevos. No toca la lista
 * original. `cambio` dice si de verdad cambió algo: un producto que vuelve a llegar igual no cuenta.
 * @returns {{ lista: object[], cambio: boolean }}
 */
export function aplicarCambios(lista, llegados) {
  const porId = new Map((lista || []).map((p) => [p.id, p]));
  let cambio = false;
  for (const p of llegados || []) {
    if (!p || !p.id) continue;
    const previo = porId.get(p.id);
    if (!p.activo) {
      if (previo) { porId.delete(p.id); cambio = true; }
      continue;
    }
    if (!previo || comparable(previo) !== comparable(p)) {
      porId.set(p.id, p);
      cambio = true;
    }
  }
  return cambio ? { lista: ordenarPorNombre([...porId.values()]), cambio } : { lista, cambio };
}

/**
 * ¿Qué se hace con la lista guardada? 'cambios' = preguntar solo lo que cambió; 'completa' = bajarla entera.
 * Completa si no hay lista (o está vacía), si no se sabe cuándo fue su última carga completa, si esa carga tiene más de
 * 24 horas o "es del futuro" (el reloj de la computadora se movió), o si su marca no es un número.
 * @param {object[]|null} lista   la lista guardada
 * @param {{ completaEn: number, marca: number|null }|null} meta  datos de la lista guardada
 * @param {number} ahoraMs
 */
export function decidirCarga(lista, meta, ahoraMs) {
  if (!Array.isArray(lista) || lista.length === 0 || !meta || typeof meta !== 'object') return 'completa';
  const edad = ahoraMs - meta.completaEn;
  if (!Number.isFinite(edad) || edad < 0 || edad > VIDA_LISTA_COMPLETA_MS) return 'completa';
  if (meta.marca !== null && !Number.isFinite(meta.marca)) return 'completa';
  return 'cambios';
}

/** Desde qué momento preguntar por cambios: la marca más nueva vista, con el margen. Sin marca, desde el principio. */
export function desdeCuando(marca) {
  return Number.isFinite(marca) ? Math.max(0, marca - MARGEN_MS) : 0;
}

/** ¿Son iguales los datos de dos listas guardadas? (para no reescribir el navegador si nada cambió) */
export function mismaMeta(a, b) {
  return !!(a && b && a.completaEn === b.completaEn && a.marca === b.marca);
}
